import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useLocalizedNavigate } from '../../hooks/useLocalizedNavigate';
import ThemedLottie from '../ui/ThemedLottie';

// Lottie source mapping (files are in /public/images):
// Reading   -> /public/images/lesen.json
// Listening -> /public/images/horen.json
// Writing   -> /public/images/schreiben.json
//
// If you move JSON files under src/assets, import them and pass as `animationData` to <ThemedLottie />.
const CARD_VISUALS = {
  reading: {
    border: 'rgba(56,189,248,0.42)',
    glow: 'rgba(56,189,248,0.35)',
    radialGlowClass: 'bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-sky-500/15 to-transparent',
    lottieSrc: '/images/lesen.json',
  },
  listening: {
    border: 'rgba(52,211,153,0.42)',
    glow: 'rgba(16,185,129,0.34)',
    radialGlowClass: 'bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-emerald-500/15 to-transparent',
    lottieSrc: '/images/horen.json',
  },
  writing: {
    border: 'rgba(251,191,36,0.42)',
    glow: 'rgba(217,119,6,0.32)',
    radialGlowClass: 'bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-amber-500/15 to-transparent',
    lottieSrc: '/images/schreiben.json',
  },
};

const ExamModePracticeHub = () => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const [activeCard, setActiveCard] = useState(null);

  const cards = useMemo(
    () => [
      {
        id: 'reading',
        title: t('dashboard.examModeHub.reading.title', { defaultValue: 'Reading Practice' }),
        subtitle: t('dashboard.examModeHub.reading.subtitle', { defaultValue: 'Practice reading tasks one by one and understand why each answer is correct.' }),
        path: '/practice/reading',
      },
      {
        id: 'listening',
        title: t('dashboard.examModeHub.listening.title', { defaultValue: 'Listening Practice' }),
        subtitle: t('dashboard.examModeHub.listening.subtitle', { defaultValue: 'Practice listening at your own pace: pause, go back, and play again anytime.' }),
        path: '/practice/listening',
      },
      {
        id: 'writing',
        title: t('dashboard.examModeHub.writing.title', { defaultValue: 'Writing Practice' }),
        subtitle: t('dashboard.examModeHub.writing.subtitle', { defaultValue: 'Write answers with Redemittel support by your side, one task at a time.' }),
        path: '/practice/writing',
      },
    ],
    [t]
  );

  return (
    <motion.div
      className="w-full"
      initial={{ opacity: 0, y: 10 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.45, ease: 'easeOut' }}
    >
      <div className="w-full rounded-2xl border border-white/10 bg-[#0B0B0B] p-2.5 sm:p-3.5">
        <div className="mb-3 px-1.5 sm:px-2">
          <h3 className="font-space text-lg font-extrabold tracking-tight text-white sm:text-xl">
            {t('dashboard.examModeHub.title', { defaultValue: 'Pick a Skill and Practice Task by Task' })}
          </h3>
        </div>

        <div className="flex w-full flex-col gap-2.5 md:flex-row md:gap-3">
          {cards.map((card) => {
            const visual = CARD_VISUALS[card.id];
            const isActive = activeCard === card.id;

            return (
              <motion.button
                key={card.id}
                type="button"
                onClick={() => navigate(card.path)}
                onHoverStart={() => setActiveCard(card.id)}
                onHoverEnd={() => setActiveCard(null)}
                onFocus={() => setActiveCard(card.id)}
                onBlur={() => setActiveCard(null)}
                whileHover={{ scale: 1.02, y: -2 }}
                whileTap={{ scale: 0.99 }}
                className="group relative flex min-h-[275px] flex-1 flex-col justify-between overflow-hidden rounded-xl border border-white/10 bg-white/[0.03] p-5 text-left transition-all duration-300 sm:min-h-[315px] sm:p-6"
                style={{
                  boxShadow: isActive
                    ? `0 0 0 1px ${visual.border}, 0 24px 50px -26px ${visual.glow}`
                    : '0 10px 30px rgba(0,0,0,0.34)',
                }}
                aria-label={card.title}
              >
                <div className="relative z-10 overflow-hidden rounded-xl border border-white/10 bg-black/30 p-2.5">
                  <motion.div
                    className={`pointer-events-none absolute inset-0 ${visual.radialGlowClass}`}
                    animate={{ opacity: isActive ? 1 : 0.7, scale: isActive ? 1.02 : 1 }}
                    transition={{ duration: 0.25, ease: 'easeOut' }}
                    aria-hidden="true"
                  />
                  <ThemedLottie
                    // For /public files, no import is needed. Just pass the public URL path.
                    src={visual.lottieSrc}
                    isActive={isActive}
                    idleSpeed={1}
                    activeSpeed={1.9}
                    className="relative z-10 h-40 w-full sm:h-48"
                  />
                </div>

                <div className="relative z-10 mt-4">
                  <h4 className="font-space text-xl font-bold tracking-tight text-white">{card.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-white/68">{card.subtitle}</p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>
    </motion.div>
  );
};

export default ExamModePracticeHub;
